import type { LocationQueryRaw } from 'vue-router'

export const DEFAULT_REDIRECT_DESTINATION = '/home'

const REDIRECT_QUERY_KEY = 'redirect'
const REDIRECT_STORAGE_KEY = 'auth-redirect-destination'

function isAuthPath(pathname: string) {
  return pathname === '/auth' || pathname.startsWith('/auth/')
}

export function getRedirectDestination(value: unknown): string {
  return resolveRedirectDestination(value, 0)
}

function resolveRedirectDestination(value: unknown, depth: number): string {
  if (
    depth >= 8 ||
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.startsWith('//')
  ) {
    return DEFAULT_REDIRECT_DESTINATION
  }

  try {
    decodeURI(value)

    const url = new URL(value, window.location.origin)
    if (url.origin !== window.location.origin || isAuthPath(url.pathname)) {
      return DEFAULT_REDIRECT_DESTINATION
    }

    if (url.pathname === '/') {
      const redirects = url.searchParams.getAll(REDIRECT_QUERY_KEY)
      return redirects.length === 1
        ? resolveRedirectDestination(redirects[0], depth + 1)
        : DEFAULT_REDIRECT_DESTINATION
    }

    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return DEFAULT_REDIRECT_DESTINATION
  }
}

export function getRedirectQuery(value: unknown): LocationQueryRaw {
  const destination = getRedirectDestination(value)

  return destination === DEFAULT_REDIRECT_DESTINATION
    ? {}
    : { [REDIRECT_QUERY_KEY]: destination }
}

export function persistRedirectDestination(value: unknown) {
  const destination = getRedirectDestination(value)

  if (destination === DEFAULT_REDIRECT_DESTINATION) {
    sessionStorage.removeItem(REDIRECT_STORAGE_KEY)
    return
  }

  sessionStorage.setItem(REDIRECT_STORAGE_KEY, destination)
}

export function getStoredRedirectDestination() {
  return getRedirectDestination(sessionStorage.getItem(REDIRECT_STORAGE_KEY))
}

export function clearStoredRedirectDestination() {
  sessionStorage.removeItem(REDIRECT_STORAGE_KEY)
}
